import React from "react";
import {
  PEOPLE_SEARCH_DEBOUNCE_MS,
  PEOPLE_SEARCH_MAX_LENGTH,
  PEOPLE_SEARCH_MIN_LENGTH,
  isSearchableQuery,
  shouldAdvancePastEmptyPage,
  type PeoplePerson,
  type PeopleSearchPage,
  type PeopleSource,
} from "@playbookai/types";
import {
  useRemoteOptions,
  type RemoteOptionsPage,
  type RemoteOptionsRequest,
} from "../../hooks/useRemoteOptions.js";

/** How long a page answers the same query again without a request (d9). */
export const PEOPLE_FIELD_CACHE_MS = 60_000;
const MAX_CACHED_PAGES = 50;
/* An empty page with a cursor is skipped at once, but not for ever: each skip
   is a request against the person's directory allowance (60 a minute), so a
   run of empty pages stops here and leaves "Load more" to the reader. */
const MAX_EMPTY_PAGE_ADVANCES = 5;

interface PersonOption {
  readonly value: string;
  readonly person: PeoplePerson;
}

export interface PeopleFieldSearch {
  /** Workspace matches, one entry per person, in the server's order. */
  readonly results: readonly PeoplePerson[];
  readonly nextCursor: string | null;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly failed: boolean;
  readonly loadMoreFailed: boolean;
  readonly loadNextPage: () => void;
  readonly retry: () => void;
}

const IDLE: Omit<PeopleFieldSearch, "loadNextPage" | "retry"> = {
  results: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  failed: false,
  loadMoreFailed: false,
};

/**
 * The whole-workspace search behind PeopleField, on the shared request engine
 * (`useRemoteOptions`): a 250 ms debounce, nothing below 2 letters, every
 * request abortable and sequence-guarded so a stale reply never replaces a
 * newer one, a cursor "Load more", and a 60 s cache per field. A page that
 * comes back empty but still has a cursor is skipped at once — it was
 * filtered server-side, and stopping there would claim "no one matches" over
 * a directory that has them.
 */
export function usePeopleFieldSearch({
  source,
  query,
  enabled,
}: {
  readonly source: PeopleSource;
  readonly query: string;
  /** The list is open. Closed, nothing is sent and what is in flight aborts. */
  readonly enabled: boolean;
}): PeopleFieldSearch {
  const normalizedQuery = query.normalize("NFKC");
  const tooLong = normalizedQuery.trim().length > PEOPLE_SEARCH_MAX_LENGTH;
  const cacheRef = React.useRef<{
    readonly source: PeopleSource;
    readonly pages: Map<string, { expiresAt: number; page: PeopleSearchPage }>;
  } | null>(null);

  const load = React.useCallback(
    async ({
      query: text,
      cursor,
      signal,
    }: RemoteOptionsRequest): Promise<RemoteOptionsPage<PersonOption>> => {
      // A new source is a new directory: its answers never serve the old one.
      if (cacheRef.current?.source !== source) {
        cacheRef.current = { source, pages: new Map() };
      }
      const cache = cacheRef.current.pages;
      const key = `${text.toLocaleLowerCase("en-US")}\u0000${cursor ?? ""}`;
      const now = Date.now();
      for (const [entry, cached] of cache) {
        if (cached.expiresAt <= now) cache.delete(entry);
      }
      let page = cache.get(key)?.page;
      if (!page) {
        page = await source.search(text, {
          ...(cursor ? { cursor } : {}),
          signal,
        });
        for (
          let skipped = 0;
          skipped < MAX_EMPTY_PAGE_ADVANCES &&
          shouldAdvancePastEmptyPage({
            isSuccess: true,
            isFetchingNextPage: false,
            lastPage: page,
          });
          skipped += 1
        ) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          page = await source.search(text, {
            cursor: page.nextCursor ?? "",
            signal,
          });
        }
        cache.delete(key);
        while (cache.size >= MAX_CACHED_PAGES) {
          const oldest = cache.keys().next().value;
          if (oldest === undefined) break;
          cache.delete(oldest);
        }
        cache.set(key, { expiresAt: Date.now() + PEOPLE_FIELD_CACHE_MS, page });
      }
      return {
        options: page.items.map((person) => ({ value: person.id, person })),
        nextCursor: page.nextCursor,
      };
    },
    [source],
  );

  const state = useRemoteOptions<PersonOption>({
    load,
    query: normalizedQuery,
    debounceMs: PEOPLE_SEARCH_DEBOUNCE_MS,
    minQueryLength: PEOPLE_SEARCH_MIN_LENGTH,
    enabled: enabled && !tooLong,
  });
  const results = React.useMemo(
    () => state.options.map((option) => option.person),
    [state.options],
  );

  // Below the minimum the engine keeps its last state; none of it applies.
  if (!enabled || !isSearchableQuery(query)) {
    return {
      ...IDLE,
      loadNextPage: state.loadNextPage,
      retry: state.retry,
    };
  }
  return {
    results,
    nextCursor: state.nextCursor,
    loading: state.loading,
    loadingMore: state.loadingMore,
    failed: state.failed,
    loadMoreFailed: state.loadMoreFailed,
    loadNextPage: state.loadNextPage,
    retry: state.retry,
  };
}
